import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

interface Base64FileUploadProps {
  onFileUpload?: (base64Url: string) => void;
  onUploadError?: (error: string) => void;
  maxSize?: number; // in MB
  allowedTypes?: string[];
  disabled?: boolean;
  accept?: string;
  label?: string;
  description?: string;
}

const Base64FileUpload: React.FC<Base64FileUploadProps> = ({
  onFileUpload,
  onUploadError,
  maxSize = 2, // 2MB default
  allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  disabled = false,
  accept,
  label = 'Качете снимка',
  description
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return {
        valid: false,
        error: `Файлът е твърде голям. Максималният размер е ${maxSize}MB.`
      };
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Типът на файла не е поддържан. Разрешени са само изображения.'
      };
    }

    return { valid: true };
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (file: File) => {
    setError(null);
    
    // Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Невалиден файл');
      onUploadError?.(validation.error || 'Невалиден файл');
      return;
    }

    setSelectedFile(file);
    setUploading(true);

    try {
      const base64String = await convertToBase64(file);
      onFileUpload?.(base64String);
    } catch (err) {
      const errorMessage = 'Грешка при обработка на файла';
      setError(errorMessage);
      onUploadError?.(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getAcceptString = () => {
    if (accept) return accept;
    return allowedTypes.join(',');
  };

  return (
    <Box>
      {/* Upload Area */}
      <Paper
        sx={{
          p: 3,
          border: `2px dashed ${dragActive ? '#28A0F0' : '#2d2d44'}`,
          backgroundColor: dragActive ? 'rgba(40, 160, 240, 0.05)' : 'transparent',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s ease',
          opacity: disabled ? 0.6 : 1,
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <Box sx={{ textAlign: 'center' }}>
          <UploadIcon 
            sx={{ 
              fontSize: 48, 
              color: dragActive ? '#28A0F0' : '#6c757d',
              mb: 2 
            }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#ffffff', 
              mb: 1,
              fontSize: { xs: '1rem', md: '1.25rem' }
            }}
          >
            {label}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#a0a0a0',
              mb: 2
            }}
          >
            {description || `Максимален размер: ${maxSize}MB | Разрешени формати: ${allowedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')}`}
          </Typography>
          <Button
            variant="outlined"
            disabled={disabled}
            sx={{
              color: '#28A0F0',
              borderColor: '#28A0F0',
              '&:hover': {
                backgroundColor: 'rgba(40, 160, 240, 0.1)',
                borderColor: '#28A0F0',
              },
            }}
          >
            Изберете файл
          </Button>
        </Box>
        
        <input
          ref={fileInputRef}
          type="file"
          accept={getAcceptString()}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={disabled}
        />
      </Paper>

      {/* Error Message */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mt: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)', color: '#ffffff' }}
        >
          {error}
        </Alert>
      )}

      {/* Selected File */}
      {selectedFile && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
            Избран файл
          </Typography>
          <Paper sx={{ backgroundColor: '#1a1a2e', borderRadius: 2, border: '1px solid #2d2d44' }}>
            <ListItem>
              <Box sx={{ mr: 2 }}>
                {uploading ? (
                  <Box sx={{ width: 24, height: 24, border: '2px solid #28A0F0', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                ) : (
                  <CheckCircleIcon sx={{ color: '#28a745' }} />
                )}
              </Box>
              <ListItemText
                primary={selectedFile.name}
                secondary={formatFileSize(selectedFile.size)}
                sx={{
                  '& .MuiListItemText-primary': { color: '#ffffff' },
                  '& .MuiListItemText-secondary': { color: '#a0a0a0' },
                }}
              />
              <ListItemSecondaryAction>
                <IconButton
                  onClick={handleRemoveFile}
                  disabled={uploading}
                  sx={{ color: '#ff6b6b' }}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          </Paper>
        </Box>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default Base64FileUpload;
